import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Users, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  UserX, 
  UserCheck, 
  Coins,
  Crown,
  Calendar,
  Mail,
  Phone,
  Shield,
  ShieldOff,
  MoreHorizontal,
  Eye,
  Download,
  Upload,
  TrendingUp,
  Activity,
  Settings,
  HelpCircle,
  User,
  Plus,
  Minus,
  Send,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { useToast } from '../../hooks/use-toast';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import adminUserService, { 
  UserSummary, 
  UserDetail, 
  UserSearchFilters,
  UserStatsResponse 
} from '../../services/adminUserService';
import EmailBroadcastDialog from '../../components/admin/EmailBroadcastDialog';

const StatCard: React.FC<{
  title: string;
  value: string | number;
  change?: string;
  icon: React.ElementType;
  color: string;
}> = ({ title, value, change, icon: Icon, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    whileHover={{ y: -2 }}
  >
    <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-0 shadow-xl hover:shadow-2xl transition-all duration-300 rounded-2xl overflow-hidden group">
      <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent dark:from-gray-700/20 dark:to-transparent" />
      <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-semibold text-slate-600 dark:text-gray-400 uppercase tracking-wide">
          {title}
        </CardTitle>
        <div className={`p-3 rounded-xl ${color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </CardHeader>
      <CardContent className="relative">
        <div className="text-3xl font-bold text-slate-800 dark:text-gray-100 mb-2">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {change && (
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
              {change}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  </motion.div>
);

const UserManagementPage: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser, refreshUser } = useAuth();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [searchFilters, setSearchFilters] = useState<UserSearchFilters>({});
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [isUserDetailOpen, setIsUserDetailOpen] = useState(false);
  const [isBulkActionOpen, setIsBulkActionOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<string>('');
  const [bulkValue, setBulkValue] = useState<string>('');
  
  // Credit management state
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [selectedUserForCredits, setSelectedUserForCredits] = useState<UserSummary | null>(null);
  const [creditOperation, setCreditOperation] = useState<'add' | 'subtract'>('add');
  const [creditAmount, setCreditAmount] = useState<string>('');
  
  // Delete confirmation state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserSummary | null>(null);
  
  // Email broadcast state
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);

  // Queries
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['admin-users', currentPage, perPage, searchFilters],
    queryFn: () => adminUserService.getUsers(currentPage, perPage, searchFilters),
  });

  const { data: userStats } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => adminUserService.getUserStats(),
  });

  // Mutations
  const updateUserMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: any }) => 
      adminUserService.updateUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast({ title: 'Success', description: 'User updated successfully' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to update user',
        variant: 'destructive'
      });
    },
  });



  const suspendUserMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: any }) => 
      adminUserService.updateUserSuspension(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast({ title: 'Success', description: 'User suspension updated' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to update suspension',
        variant: 'destructive'
      });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => adminUserService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast({ title: 'Success', description: 'User deleted successfully' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to delete user',
        variant: 'destructive'
      });
    },
  });

  const bulkActionMutation = useMutation({
    mutationFn: (actionData: any) => adminUserService.bulkUserAction(actionData),
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      
      // If the bulk action affects the current user and involves credits, refresh user data
      if (currentUser && variables.user_ids.includes(currentUser.id) && 
          (variables.action === 'add_credits' || variables.action === 'remove_credits')) {
        await refreshUser();
      }
      
      setSelectedUsers([]);
      setIsBulkActionOpen(false);
      toast({ 
        title: 'Success', 
        description: `${data.users_affected} users updated successfully` 
      });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to perform bulk action',
        variant: 'destructive'
      });
    },
  });

  const updateCreditsMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: any }) => 
      adminUserService.updateUserCredits(userId, data),
    onSuccess: async (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      
      // If the updated user is the current logged-in user, refresh their data
      if (currentUser && userId === currentUser.id) {
        await refreshUser();
      }
      
      setIsCreditModalOpen(false);
      setSelectedUserForCredits(null);
      setCreditAmount('');
      toast({ 
        title: 'Success', 
        description: 'Credits updated successfully' 
      });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to update credits',
        variant: 'destructive'
      });
    },
  });

  const createClientMutation = useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      adminUserService.createDefaultClientForUser(userId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast({
        title: 'Success',
        description: `Created client "${data.client.name}" for user`
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create client',
        variant: 'destructive'
      });
    },
  });

  const toggleBetaTesterMutation = useMutation({
    mutationFn: ({ userId, isBetaTester }: { userId: string; isBetaTester: boolean }) =>
      adminUserService.toggleBetaTesterStatus(userId, isBetaTester),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast({
        title: 'Success',
        description: data.message
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to toggle beta tester status',
        variant: 'destructive'
      });
    },
  });

  const handleUserSelect = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(usersData?.users.map(user => user.id) || []);
    } else {
      setSelectedUsers([]);
    }
  };

  const handleViewUser = async (userId: string) => {
    try {
      const userDetail = await adminUserService.getUserDetail(userId);
      setSelectedUser(userDetail);
      setIsUserDetailOpen(true);
    } catch (error: any) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to load user details',
        variant: 'destructive'
      });
    }
  };

  const handleBulkAction = () => {
    if (!bulkAction || selectedUsers.length === 0) return;

    const actionData: any = {
      user_ids: selectedUsers,
      action: bulkAction,
    };

    if (bulkAction.includes('credits') && bulkValue) {
      actionData.value = bulkValue;
      actionData.reason = `Bulk ${bulkAction} by admin`;
    }

    bulkActionMutation.mutate(actionData);
  };

  const handleOpenCreditModal = (user: UserSummary) => {
    setSelectedUserForCredits(user);
    setIsCreditModalOpen(true);
    setCreditOperation('add');
    setCreditAmount('');
  };

  const handleUpdateCredits = () => {
    if (!selectedUserForCredits || !creditAmount) return;

    const amount = parseFloat(creditAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: 'Error',
        description: 'Please enter a valid amount',
        variant: 'destructive'
      });
      return;
    }

    const finalAmount = creditOperation === 'subtract' ? -amount : amount;
    const newCredits = selectedUserForCredits.credits + finalAmount;

    if (newCredits < 0) {
      toast({
        title: 'Error',
        description: 'Cannot subtract more credits than the user has',
        variant: 'destructive'
      });
      return;
    }

    updateCreditsMutation.mutate({
      userId: selectedUserForCredits.id,
      data: {
        credits: newCredits,
        reason: `${creditOperation === 'add' ? 'Added' : 'Subtracted'} ${amount} credits by admin`
      }
    });
  };

  const handleDeleteUser = () => {
    if (!userToDelete) return;
    
    deleteUserMutation.mutate(userToDelete.id);
    setIsDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleCreateClientForUser = (user: UserSummary) => {
    createClientMutation.mutate({ userId: user.id });
  };

  const openDeleteDialog = (user: UserSummary) => {
    if (currentUser?.id === user.id) {
      toast({
        title: 'Error',
        description: 'You cannot delete your own account',
        variant: 'destructive'
      });
      return;
    }
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const getStatusBadge = (user: UserSummary) => {
    if (!user.is_active) {
      // Check if user is deleted using the backend flag
      if (user.is_deleted) {
        return <Badge variant="destructive" className="bg-red-600 text-white">Deleted</Badge>;
      }
      return <Badge variant="destructive" className="bg-orange-600 text-white">Suspended</Badge>;
    }
    if (user.is_admin) {
      return <Badge className="bg-purple-100 text-purple-800">Admin</Badge>;
    }
    if (user.is_beta_tester) {
      return <Badge className="bg-amber-100 text-amber-800 flex items-center gap-1"><Sparkles className="w-3 h-3" />Beta Tester</Badge>;
    }
    return <Badge variant="secondary">Active</Badge>;
  };

  const getTierBadge = (tierName?: string) => {
    if (!tierName) return <Badge variant="outline">No Tier</Badge>;
    
    const colors: Record<string, string> = {
      'free': '!bg-gray-200 !text-gray-900 dark:!bg-gray-700 dark:!text-gray-200 border-gray-300 dark:border-gray-600',
      'pro': '!bg-blue-200 !text-blue-900 dark:!bg-blue-900/50 dark:!text-blue-200 border-blue-300 dark:border-blue-600',
      'enterprise': '!bg-purple-200 !text-purple-900 dark:!bg-purple-900/50 dark:!text-purple-200 border-purple-300 dark:border-purple-600',
    };
    
    const colorClass = colors[tierName.toLowerCase()] || '!bg-indigo-200 !text-indigo-900 dark:!bg-indigo-900/50 dark:!text-indigo-200 border-indigo-300 dark:border-indigo-600';
    
    return <Badge className={colorClass}>{tierName}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900">
      {/* Modern Header */}
      <div className="backdrop-blur-2xl bg-white/60 dark:bg-slate-900/60 border-b border-slate-200/50 dark:border-slate-700/30">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <motion.div 
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-black bg-gradient-to-r from-gray-900 via-blue-600 to-purple-600 bg-clip-text text-transparent dark:from-white dark:via-blue-300 dark:to-purple-300">
                  User Management
                </h1>
                <p className="text-slate-600 dark:text-slate-300">Manage users, credits, and permissions</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button 
                onClick={() => setIsEmailDialogOpen(true)}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg hover:shadow-emerald-500/25 transition-all duration-300"
              >
                <Send className="mr-2 h-4 w-4" />
                Send Email
              </Button>
              
              {selectedUsers.length > 0 && (
                <Dialog open={isBulkActionOpen} onOpenChange={setIsBulkActionOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg hover:shadow-blue-500/25 transition-all duration-300"
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      Bulk Actions ({selectedUsers.length})
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border-0 shadow-2xl">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                        Bulk Actions
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Action</Label>
                        <Select value={bulkAction} onValueChange={setBulkAction}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select action" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="activate">Activate Users</SelectItem>
                            <SelectItem value="deactivate">Deactivate Users</SelectItem>
                            <SelectItem value="suspend">Suspend Users</SelectItem>
                            <SelectItem value="add_credits">Add Credits</SelectItem>
                            <SelectItem value="remove_credits">Remove Credits</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {bulkAction.includes('credits') && (
                        <div>
                          <Label>Credits Amount</Label>
                          <Input
                            type="number"
                            value={bulkValue}
                            onChange={(e) => setBulkValue(e.target.value)}
                            placeholder="Enter amount"
                          />
                        </div>
                      )}
                      <Button 
                        onClick={handleBulkAction}
                        disabled={!bulkAction || bulkActionMutation.isPending}
                        className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                      >
                        {bulkActionMutation.isPending ? 'Processing...' : 'Execute Action'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
              <Button 
                variant="outline" 
                size="sm"
                className="text-slate-600 border-slate-300/50 bg-white/50 hover:bg-slate-100/50 backdrop-blur-sm shadow-lg shadow-slate-200/50 dark:text-slate-300 dark:border-slate-600/50 dark:bg-transparent dark:hover:bg-slate-800/30 dark:shadow-none"
              >
                <HelpCircle className="mr-2 h-4 w-4" />
                Help
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto p-8 space-y-8">

        {/* Stats Cards */}
        {userStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <StatCard
              title="Total Users"
              value={userStats.total_users}
              change={`${userStats.active_users} active`}
              icon={Users}
              color="bg-blue-500"
            />
            <StatCard
              title="Active Users"
              value={userStats.active_users}
              change="Last 7 days"
              icon={UserCheck}
              color="bg-emerald-500"
            />
            <StatCard
              title="Suspended"
              value={userStats.suspended_users}
              change="Requires attention"
              icon={UserX}
              color="bg-red-500"
            />
            <StatCard
              title="Total Credits"
              value={userStats.total_credits_distributed.toFixed(0)}
              change="Distributed"
              icon={Coins}
              color="bg-amber-500"
            />
          </div>
        )}

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border-0 shadow-xl"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent dark:from-gray-700/20 dark:to-transparent rounded-2xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-slate-700 dark:text-gray-300 font-semibold">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search users..."
                  value={searchFilters.search || ''}
                  onChange={(e) => setSearchFilters({ ...searchFilters, search: e.target.value })}
                  className="pl-10 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border-slate-200/50 dark:border-gray-600/50"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-700 dark:text-gray-300 font-semibold">Status</Label>
              <Select 
                value={searchFilters.is_active?.toString() || 'all'} 
                onValueChange={(value) => setSearchFilters({ 
                  ...searchFilters, 
                  is_active: value === 'all' ? undefined : value === 'true' 
                })}
              >
                <SelectTrigger className="bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border-slate-200/50 dark:border-gray-600/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700 dark:text-gray-300 font-semibold">Role</Label>
              <Select 
                value={searchFilters.is_admin?.toString() || 'all'} 
                onValueChange={(value) => setSearchFilters({ 
                  ...searchFilters, 
                  is_admin: value === 'all' ? undefined : value === 'true' 
                })}
              >
                <SelectTrigger className="bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border-slate-200/50 dark:border-gray-600/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="false">Users</SelectItem>
                  <SelectItem value="true">Admins</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                onClick={() => setCurrentPage(1)}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 shadow-lg"
              >
                <Filter className="w-4 h-4 mr-2" />
                Apply Filters
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Users Table */}
        {usersData && (
          <motion.div 
            className="relative rounded-3xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 via-indigo-50/30 to-purple-50/30 dark:from-indigo-500/5 dark:via-purple-500/5 dark:to-slate-500/5"></div>
            
            {/* Table Header */}
            <div className="relative border-b border-slate-200/60 dark:border-slate-600/40 px-6 py-4">
              <div className="grid grid-cols-12 gap-6 items-center">
                <div className="col-span-3 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={selectedUsers.length === usersData.users.length && usersData.users.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                    <span>User Details</span>
                  </div>
                </div>
                <div className="col-span-1 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider text-center">
                  Credits
                </div>
                <div className="col-span-2 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider text-center">
                  Clients
                </div>
                <div className="col-span-2 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider text-center">
                  Tier
                </div>
                <div className="col-span-2 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider text-center">
                  Status
                </div>
                <div className="col-span-2 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider text-right">
                  Actions
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div className="relative">
              {usersLoading ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Activity className="w-6 h-6 text-white animate-pulse" />
                  </div>
                  <p className="text-slate-600 dark:text-gray-400 font-medium">Loading users...</p>
                </div>
              ) : (
                <>
                  {usersData.users
                    .sort((a, b) => {
                      // Sort by admin status first (admins first)
                      if (a.is_admin && !b.is_admin) return -1;
                      if (!a.is_admin && b.is_admin) return 1;
                      // Then sort by name/email alphabetically
                      const aName = a.first_name && a.last_name ? `${a.first_name} ${a.last_name}` : a.email;
                      const bName = b.first_name && b.last_name ? `${b.first_name} ${b.last_name}` : b.email;
                      return aName.localeCompare(bName);
                    })
                    .map((user, index) => {
                    const getUserIcon = (user: UserSummary) => {
                      if (user.is_admin) return { Icon: Crown, color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-500/10' };
                      if (user.tier_display_name?.toLowerCase().includes('pro')) return { Icon: Shield, color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-500/10' };
                      return { Icon: User, color: 'text-slate-600 dark:text-slate-400', bgColor: 'bg-slate-500/10' };
                    };
                    
                    const { Icon, color, bgColor } = getUserIcon(user);
                    
                    return (
                      <motion.div
                        key={user.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.05 }}
                        className="group relative border-b border-slate-200/40 dark:border-slate-600/30 last:border-b-0 hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-all duration-200"
                      >
                        <div className="px-6 py-5">
                          <div className="grid grid-cols-12 gap-6 items-center">
                            {/* User Details */}
                            <div className="col-span-3 flex items-center space-x-4">
                              <Checkbox
                                checked={selectedUsers.includes(user.id)}
                                onCheckedChange={(checked) => handleUserSelect(user.id, checked as boolean)}
                              />
                              <div className={`w-10 h-10 ${bgColor} rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-200`}>
                                <Icon className={`w-5 h-5 ${color}`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 truncate">
                                  {user.first_name && user.last_name 
                                    ? `${user.first_name} ${user.last_name}` 
                                    : user.email}
                                </h3>
                                <div className="flex items-center space-x-2 text-sm text-slate-500 dark:text-slate-400">
                                  <Mail className="w-3 h-3" />
                                  <span className="truncate">{user.email}</span>
                                </div>
                              </div>
                            </div>

                            {/* Credits */}
                            <div className="col-span-1 text-center">
                              <div className="flex flex-col items-center space-y-1">
                                <div className="flex items-center space-x-1">
                                  <Coins className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                  <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                                    {user.credits}
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenCreditModal(user)}
                                  className="transition-all duration-200 hover:bg-amber-100 dark:hover:bg-amber-900/20 hover:text-amber-600 dark:hover:text-amber-400 hover:scale-105 p-1 h-6 w-6"
                                  title="Manage Credits"
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>

                            {/* Clients */}
                            <div className="col-span-2 text-center">
                              {user.clients && user.clients.length > 0 ? (
                                <div className="space-y-1">
                                  {user.clients.map((client) => (
                                    <Badge key={client.id} variant="outline" className="text-xs block">
                                      {client.name}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex flex-col items-center space-y-1">
                                  <Badge variant="destructive" className="text-xs">No Client</Badge>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleCreateClientForUser(user)}
                                    className="h-6 w-6 p-0 hover:bg-blue-100 dark:hover:bg-blue-900/20"
                                    title="Create Client"
                                  >
                                    <Plus className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                                  </Button>
                                </div>
                              )}
                            </div>

                            {/* Tier */}
                            <div className="col-span-2 flex justify-center">
                              {getTierBadge(user.tier_display_name)}
                            </div>

                            {/* Status */}
                            <div className="col-span-2 text-center">
                              <div className="text-center">
                                {getStatusBadge(user)}
                                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                  {new Date(user.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="col-span-2 flex items-center justify-end space-x-2">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleViewUser(user.id)}
                                className="transition-all duration-200 hover:bg-blue-100 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 hover:scale-105"
                                title="View User Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="transition-all duration-200 hover:bg-slate-100 dark:hover:bg-slate-700 hover:scale-105"
                                    title="More Actions"
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-48 z-50 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border-0 shadow-2xl" align="end" side="bottom" sideOffset={5}>
                                  <div className="space-y-1 p-1">
                                  <button 
                                    onClick={() => {
                                      if (currentUser?.id === user.id) {
                                        toast({
                                          title: 'Error',
                                          description: 'You cannot suspend your own account',
                                          variant: 'destructive'
                                        });
                                        return;
                                      }
                                      suspendUserMutation.mutate({ 
                                        userId: user.id, 
                                        data: { 
                                          is_suspended: user.is_active,
                                          suspension_reason: user.is_active ? 'Suspended by admin' : undefined
                                        } 
                                      });
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-600 rounded-md flex items-center cursor-pointer ${currentUser?.id === user.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    disabled={suspendUserMutation.isPending || currentUser?.id === user.id}
                                  >
                                    {user.is_active ? (
                                      <>
                                        <UserX className="w-4 h-4 mr-2" />
                                        {suspendUserMutation.isPending ? 'Suspending...' : 'Suspend User'}
                                      </>
                                    ) : (
                                      <>
                                        <UserCheck className="w-4 h-4 mr-2" />
                                        {suspendUserMutation.isPending ? 'Activating...' : 'Activate User'}
                                      </>
                                    )}
                                  </button>
                                  <div className="border-t border-slate-200 dark:border-slate-600 my-1"></div>
                                  <button 
                                    onClick={() => {
                                      if (currentUser?.id === user.id && user.is_admin) {
                                        toast({
                                          title: 'Error',
                                          description: 'You cannot remove your own admin privileges',
                                          variant: 'destructive'
                                        });
                                        return;
                                      }
                                      updateUserMutation.mutate({ 
                                        userId: user.id, 
                                        data: { is_admin: !user.is_admin } 
                                      });
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-600 rounded-md flex items-center cursor-pointer ${currentUser?.id === user.id && user.is_admin ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    disabled={updateUserMutation.isPending || (currentUser?.id === user.id && user.is_admin)}
                                  >
                                    {user.is_admin ? (
                                      <>
                                        <ShieldOff className="w-4 h-4 mr-2" />
                                        {updateUserMutation.isPending ? 'Removing...' : 'Remove Admin'}
                                      </>
                                    ) : (
                                      <>
                                        <Shield className="w-4 h-4 mr-2" />
                                        {updateUserMutation.isPending ? 'Making Admin...' : 'Make Admin'}
                                      </>
                                    )}
                                  </button>
                                  <button 
                                    onClick={() => {
                                      toggleBetaTesterMutation.mutate({ 
                                        userId: user.id, 
                                        isBetaTester: !user.is_beta_tester 
                                      });
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-600 rounded-md flex items-center cursor-pointer"
                                    disabled={toggleBetaTesterMutation.isPending}
                                  >
                                    {user.is_beta_tester ? (
                                      <>
                                        <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
                                        {toggleBetaTesterMutation.isPending ? 'Removing...' : 'Remove Beta Access'}
                                      </>
                                    ) : (
                                      <>
                                        <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
                                        {toggleBetaTesterMutation.isPending ? 'Granting...' : 'Grant Beta Access'}
                                      </>
                                    )}
                                  </button>
                                  <div className="border-t border-slate-200 dark:border-slate-600 my-1"></div>
                                  <button 
                                    onClick={() => openDeleteDialog(user)}
                                    className={`w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md flex items-center cursor-pointer ${currentUser?.id === user.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    disabled={deleteUserMutation.isPending || currentUser?.id === user.id}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    {deleteUserMutation.isPending ? 'Deleting...' : 'Delete User'}
                                  </button>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Pagination */}
            {usersData && usersData.total_pages > 1 && (
              <div className="relative border-t border-slate-200/60 dark:border-slate-600/40 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-slate-600 dark:text-gray-400 font-medium">
                    Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, usersData.total)} of {usersData.total} users
                  </div>
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="bg-white/50 hover:bg-slate-100/50 backdrop-blur-sm border-slate-200/50 dark:bg-gray-700/50 dark:hover:bg-gray-600/50 dark:border-gray-600/50"
                    >
                      Previous
                    </Button>
                    <span className="text-sm font-semibold text-slate-700 dark:text-gray-300 px-3 py-1 bg-slate-100/50 dark:bg-gray-700/50 rounded-lg">
                      Page {currentPage} of {usersData.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.min(usersData.total_pages, currentPage + 1))}
                      disabled={currentPage === usersData.total_pages}
                      className="bg-white/50 hover:bg-slate-100/50 backdrop-blur-sm border-slate-200/50 dark:bg-gray-700/50 dark:hover:bg-gray-600/50 dark:border-gray-600/50"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Credit Management Modal */}
        <Dialog open={isCreditModalOpen} onOpenChange={setIsCreditModalOpen}>
          <DialogContent className="max-w-md bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border-0 shadow-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                Manage Credits
              </DialogTitle>
            </DialogHeader>
            {selectedUserForCredits && (
              <div className="space-y-6">
                {/* User Info */}
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {(selectedUserForCredits.first_name || selectedUserForCredits.email).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-amber-800 dark:text-amber-200">
                        {selectedUserForCredits.first_name && selectedUserForCredits.last_name 
                          ? `${selectedUserForCredits.first_name} ${selectedUserForCredits.last_name}` 
                          : selectedUserForCredits.email}
                      </p>
                      <p className="text-sm text-amber-600 dark:text-amber-400">
                        Current Balance: <span className="font-bold">{selectedUserForCredits.credits} credits</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Operation Selection */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Operation Type
                  </Label>
                  <RadioGroup
                    value={creditOperation}
                    onValueChange={(value: 'add' | 'subtract') => setCreditOperation(value)}
                    className="grid grid-cols-2 gap-3"
                  >
                    <div className="flex items-center space-x-2 p-3 border border-slate-200 dark:border-slate-600 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                      <RadioGroupItem value="add" id="add-credits" />
                      <Label htmlFor="add-credits" className="flex items-center space-x-2 cursor-pointer">
                        <Plus className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        <span className="text-slate-700 dark:text-slate-300">Add Credits</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 border border-slate-200 dark:border-slate-600 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                      <RadioGroupItem value="subtract" id="subtract-credits" />
                      <Label htmlFor="subtract-credits" className="flex items-center space-x-2 cursor-pointer">
                        <Minus className="w-4 h-4 text-red-600 dark:text-red-400" />
                        <span className="text-slate-700 dark:text-slate-300">Subtract Credits</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Amount Input */}
                <div className="space-y-2">
                  <Label htmlFor="credit-amount" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Credit Amount
                  </Label>
                  <div className="relative">
                    <Coins className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-amber-500" />
                    <Input
                      id="credit-amount"
                      type="number"
                      step="0.1"
                      min="0.1"
                      placeholder="Enter amount"
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(e.target.value)}
                      className="pl-10 bg-white/50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 dark:focus:border-amber-400"
                    />
                  </div>
                </div>

                {/* Preview */}
                {creditAmount && (
                  <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      New balance will be: <span className="font-bold text-slate-800 dark:text-slate-200">
                        {creditOperation === 'add' 
                          ? selectedUserForCredits.credits + parseFloat(creditAmount || '0')
                          : selectedUserForCredits.credits - parseFloat(creditAmount || '0')
                        } credits
                      </span>
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setIsCreditModalOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpdateCredits}
                    disabled={updateCreditsMutation.isPending || !creditAmount}
                    className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700"
                  >
                    {updateCreditsMutation.isPending ? 'Updating...' : 'Update Credits'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* User Detail Dialog */}
        <Dialog open={isUserDetailOpen} onOpenChange={setIsUserDetailOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border-0 shadow-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                User Details
              </DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50/50 dark:bg-gray-700/50 rounded-xl">
                    <Label className="text-slate-600 dark:text-gray-400 font-semibold">Name</Label>
                    <p className="font-semibold text-slate-800 dark:text-gray-100 mt-1">
                      {selectedUser.first_name && selectedUser.last_name 
                        ? `${selectedUser.first_name} ${selectedUser.last_name}` 
                        : 'Not provided'}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50/50 dark:bg-gray-700/50 rounded-xl">
                    <Label className="text-slate-600 dark:text-gray-400 font-semibold">Email</Label>
                    <p className="font-semibold text-slate-800 dark:text-gray-100 mt-1">{selectedUser.email}</p>
                  </div>
                  <div className="p-4 bg-slate-50/50 dark:bg-gray-700/50 rounded-xl">
                    <Label className="text-slate-600 dark:text-gray-400 font-semibold">Phone</Label>
                    <p className="font-semibold text-slate-800 dark:text-gray-100 mt-1">{selectedUser.phone || 'Not provided'}</p>
                  </div>
                  <div className="p-4 bg-slate-50/50 dark:bg-gray-700/50 rounded-xl">
                    <Label className="text-slate-600 dark:text-gray-400 font-semibold">Credits</Label>
                    <p className="font-semibold text-slate-800 dark:text-gray-100 mt-1">{selectedUser.credits}</p>
                  </div>
                  <div className="p-4 bg-slate-50/50 dark:bg-gray-700/50 rounded-xl">
                    <Label className="text-slate-600 dark:text-gray-400 font-semibold">Tier</Label>
                    <p className="font-semibold text-slate-800 dark:text-gray-100 mt-1">{selectedUser.tier_display_name || 'No tier'}</p>
                  </div>
                  <div className="p-4 bg-slate-50/50 dark:bg-gray-700/50 rounded-xl">
                    <Label className="text-slate-600 dark:text-gray-400 font-semibold">Status</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      {getStatusBadge(selectedUser)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200/50 dark:border-blue-700/50">
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{selectedUser.total_posts}</p>
                    <p className="text-sm text-blue-700 dark:text-blue-300 font-medium mt-1">Total Posts</p>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200/50 dark:border-green-700/50">
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{selectedUser.total_media_files}</p>
                    <p className="text-sm text-green-700 dark:text-green-300 font-medium mt-1">Media Files</p>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-xl border border-purple-200/50 dark:border-purple-700/50">
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{selectedUser.total_ai_tasks}</p>
                    <p className="text-sm text-purple-700 dark:text-purple-300 font-medium mt-1">AI Tasks</p>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <Button
                    onClick={() => {
                      const credits = prompt('Enter new credit amount:', selectedUser.credits.toString());
                      const reason = prompt('Enter reason for credit change:');
                      if (credits && reason) {
                        updateCreditsMutation.mutate({
                          userId: selectedUser.id,
                          data: { credits: parseFloat(credits), reason }
                        });
                      }
                    }}
                    className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 shadow-lg"
                  >
                    <Coins className="w-4 h-4 mr-2" />
                    Update Credits
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => updateUserMutation.mutate({
                      userId: selectedUser.id,
                      data: { is_active: !selectedUser.is_active }
                    })}
                    className="flex-1 bg-white/50 hover:bg-slate-100/50 backdrop-blur-sm border-slate-200/50 dark:bg-gray-700/50 dark:hover:bg-gray-600/50 dark:border-gray-600/50"
                  >
                    {selectedUser.is_active ? (
                      <>
                        <UserX className="w-4 h-4 mr-2" />
                        Suspend
                      </>
                    ) : (
                      <>
                        <UserCheck className="w-4 h-4 mr-2" />
                        Activate
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="max-w-md bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border-0 shadow-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-red-600 dark:text-red-400 flex items-center">
                <Trash2 className="w-5 h-5 mr-2" />
                Delete User Account
              </DialogTitle>
            </DialogHeader>
            
            {userToDelete && (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-red-900 dark:text-red-100">
                        {userToDelete.first_name && userToDelete.last_name 
                          ? `${userToDelete.first_name} ${userToDelete.last_name}` 
                          : userToDelete.email}
                      </p>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        {userToDelete.email}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-slate-800 dark:text-slate-200 font-medium">
                    Are you sure you want to delete this user account?
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    This action cannot be undone. The user will lose access to their account and all associated data.
                  </p>
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setIsDeleteDialogOpen(false)}
                    className="flex-1"
                    disabled={deleteUserMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteUser}
                    disabled={deleteUserMutation.isPending}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {deleteUserMutation.isPending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete User
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Email Broadcast Dialog */}
        <EmailBroadcastDialog
          isOpen={isEmailDialogOpen}
          onClose={() => setIsEmailDialogOpen(false)}
          selectedUserIds={selectedUsers}
          currentFilters={searchFilters}
          totalUsers={usersData?.total || 0}
        />
      </div>
    </div>
  );
};

export default UserManagementPage;